import React, { useEffect, useState } from "react";
import { MenuData } from "./MenuData";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import favicon from "./../../assets/images/positivo.png";
import { HiMenuAlt2, HiX } from "react-icons/hi";

export default function SidebarPremium() {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const toggleSidebar = () => setOpen((v) => !v);
  const closeSidebar = () => setOpen(false);

  const goHome = () => navigate("/");

  const isActive = (url) => location.pathname === url;

  // Fecha com ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeSidebar();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const drawerVariants = {
    hidden: { x: "-100%" },
    visible: { x: 0 },
  };

  return (
    <>
      {/* BOTÃO MOBILE (glass) */}
      <button
        onClick={toggleSidebar}
        aria-label={open ? "Fechar menu" : "Abrir menu"}
        className="
          lg:hidden fixed top-4 left-4 z-[60]
          bg-white/10 backdrop-blur-md
          border border-white/20
          text-white p-2.5 rounded-xl
          shadow-[0_12px_40px_rgba(0,0,0,0.35)]
          hover:bg-white/15 active:scale-[0.98]
          transition
        "
      >
        {open ? <HiX size={26} /> : <HiMenuAlt2 size={26} />}
      </button>

      {/* DESKTOP SIDEBAR */}
      <div className="hidden lg:flex">
        <SidebarContent
          goHome={goHome}
          navigate={navigate}
          isActive={isActive}
        />
      </div>

      {/* MOBILE: OVERLAY + DRAWER */}
      <AnimatePresence>
        {open && (
          <>
            {/* Overlay (clica fora e fecha) */}
            <motion.button
              type="button"
              aria-label="Fechar menu (fundo)"
              onClick={closeSidebar}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Drawer */}
            <motion.div
              className="
                fixed top-0 left-0 z-[55]
                h-full
                w-[86vw] max-w-[320px]
                text-white
                shadow-[0_20px_80px_rgba(0,0,0,0.45)]
              "
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <SidebarContent
                goHome={() => {
                  goHome();
                  closeSidebar();
                }}
                navigate={(url) => {
                  navigate(url);
                  closeSidebar();
                }}
                isActive={isActive}
                mobile
                onClose={closeSidebar}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ------------------------------------------------------------------
   CONTEÚDO (reutilizável)
------------------------------------------------------------------- */
function SidebarContent({ goHome, navigate, isActive, mobile = false, onClose }) {
  return (
    <div
      className="
        h-full w-full
        bg-gradient-to-b from-[#7A1111] via-[#B11212] to-[#5A0D0D]
        text-white
        flex flex-col
        border-r border-white/10
      "
    >
      {/* HEADER (sticky) */}
      <div className="sticky top-0 z-10">
        <div className="px-5 pt-5 pb-4 border-b border-white/10 bg-black/10 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <motion.img
              src={favicon}
              alt="Logo"
              className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 shadow-md object-contain p-1"
              whileHover={{ rotate: 5, scale: 1.03 }}
              transition={{ type: "spring", stiffness: 220 }}
            />

            <div className="min-w-0">
              <h1 className="text-[15px] font-extrabold tracking-wide truncate">
                Admin
              </h1>
              <p className="text-[11px] text-white/80 truncate">
                Congolinaria Empório
              </p>
            </div>

            {/* X extra no mobile (opcional) */}
            {mobile && (
              <button
                onClick={onClose}
                aria-label="Fechar"
                className="ml-auto rounded-xl p-2 hover:bg-white/10 transition"
              >
                <HiX size={22} />
              </button>
            )}
          </div>

          {/* linha decorativa */}
          <div className="mt-4 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
        </div>
      </div>

      {/* MENU */}
      <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {MenuData.map((item, index) => {
          const active = isActive(item.url);

          return (
            <motion.button
              key={index}
              onClick={() => navigate(item.url)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left
                transition-all duration-300 relative
                border
                ${
                  active
                    ? "bg-white text-[#8B1111] border-white/30 shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
                    : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                }
              `}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Glow + marcador ativo */}
              {active && (
                <>
                  <motion.div
                    layoutId="sidebarActiveGlow"
                    className="absolute inset-0 rounded-2xl bg-white/10 blur-[18px]"
                  />
                  <motion.div
                    layoutId="sidebarActiveBar"
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1.5 bg-white rounded-r-xl"
                  />
                </>
              )}

              <i
                className={`${item.icone} text-lg ${
                  active ? "text-[#8B1111]" : "text-white/90"
                }`}
              />

              <span className={`text-sm ${active ? "font-extrabold" : "font-semibold"}`}>
                {item.title}
              </span>

              {/* seta discreta */}
              <span
                className={`ml-auto text-xs ${
                  active ? "text-[#8B1111]/70" : "text-white/60"
                }`}
              >
                →
              </span>
            </motion.button>
          );
        })}
      </nav>

      {/* FOOTER */}
      <footer className="p-4 border-t border-white/10 bg-black/10 backdrop-blur-md">
        <motion.button
          onClick={goHome}
          className="
            w-full
            bg-white text-[#8B1111]
            font-extrabold
            px-4 py-2.5 rounded-2xl
            hover:bg-white/95
            shadow-[0_12px_30px_rgba(0,0,0,0.25)]
            transition
          "
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Voltar ao Site
        </motion.button>

        <p className="mt-3 text-[11px] text-white/70 text-center">
          Painel interno • acesso restrito
        </p>
      </footer>
    </div>
  );
}
